import { faHelmetSafety } from '@fortawesome/pro-solid-svg-icons';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { cn } from 'src/common/utils';
import { Badge } from 'src/common/components/Badge';
import { Card } from 'src/common/components/Card';
import { Image } from 'src/common/components/image/Image';
import {
  LicenseType,
  ProjectWithDownloadRef,
} from 'src/project/types/projects';
import { getSubmittedWorkdaysCount } from 'src/project/api/projectsApi';
import projectThumbnail from 'src/assets/project-thumb.svg';

export const ProjectListItem = ({
  project,
}: {
  project: ProjectWithDownloadRef;
}) => {
  const { t } = useTranslation('project');

  const meta = {
    street: project.projectInformation?.street,
    area: getLocation(project.projectInformation),
  };

  const licenseType = project.license?.type;

  const isExpired = Boolean(
    project.license?.expirationDate &&
      (licenseType === LicenseType.BASIC
        ? dayjs(project.license?.expirationDate)
            .startOf('day')
            .isBefore(dayjs().startOf('day'))
        : licenseType === LicenseType.FREE
        ? dayjs(project.license?.expirationDate).isBefore(dayjs())
        : false),
  );

  const daysLeft =
    dayjs(project.license?.expirationDate).diff(dayjs(), 'day') + 1;

  const { data: submittedWorkdays } = useQuery({
    queryKey: ['submittedWorkdaysCount', project._id],
    queryFn: () => getSubmittedWorkdaysCount(project._id),
  });

  const submittedCount = submittedWorkdays?.count ?? 0;

  return (
    <Link to={`/p/${project._id}`}>
      <Card className="min-h-[220px] border border-transparent transition-colors hover:border-blue-600">
        <div className="relative">
          <div className="h-28 overflow-hidden bg-gray-200">
            <Image
              loading="lazy"
              src={project.downloadRef?.downloadUrl || projectThumbnail}
              className={cn('h-full w-full object-cover object-center', {
                grayScale: isExpired,
              })}
            />
          </div>
          <div className="absolute left-0 top-0 flex flex-col items-end gap-1 p-2">
            {isExpired ? (
              <Badge
                icon={faHelmetSafety}
                size="md"
                className="rounded-md bg-[#292F36CC] text-[#F65C52]"
                uppercase={false}
              >
                {t('listView.archived.label')}
              </Badge>
            ) : (
              licenseType === LicenseType.BASIC && (
                <Badge
                  icon={faHelmetSafety}
                  size="md"
                  className="rounded-md bg-[#292F36CC] text-[#F9BC47]"
                  uppercase={false}
                >
                  {daysLeft <= 15 &&
                    daysLeft > 0 &&
                    t('listView.daysLeft', { daysLeft })}
                </Badge>
              )
            )}
          </div>
          {submittedCount > 0 && (
            <div className="group absolute right-0 top-0 p-2">
              <Badge
                icon={faEnvelope}
                size="md"
                className="rounded-md bg-[#292F36CC] text-[#F9BC47]"
                uppercase={false}
              >
                <span className="hidden group-hover:inline">
                  {submittedCount} {t('listView.pendingApproval')}
                </span>
              </Badge>
            </div>
          )}
        </div>
        <div className="p-4">
          <h3 className="mb-2 font-semibold">{project.name}</h3>
          <div className="text-shuttleGray-600">
            <div>{meta.street}</div>
            <div>{meta.area}</div>
          </div>
        </div>
      </Card>
    </Link>
  );
};

function getLocation(info: ProjectWithDownloadRef['projectInformation']) {
  if (!info) {
    return null;
  }
  const addressLine = [info.zipCode, info.city].filter(Boolean).join(' ');
  return [addressLine, info.country].filter(Boolean).join(', ');
}
